import { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import cronstrue from 'cronstrue';
import 'cronstrue/locales/es';
import {
  COUNTRY,
  COUNTRY_LABEL,
  COUNTRY_CODES,
  utcTimeInCountry,
} from '@/config/country';

const cronFields = [
  { name: 'minutes', label: 'Minutos', maxLength: 2, maxValue: 59 },
  { name: 'hours', label: 'Horas', maxLength: 2, maxValue: 23 },
  { name: 'dayOfMonth', label: 'Día del mes', maxLength: 2, maxValue: 31 },
  { name: 'month', label: 'Mes', maxLength: 2, maxValue: 12 },
  { name: 'dayOfWeek', label: 'Día de la semana', maxLength: 1, maxValue: 6 },
] as const;

type CronField = (typeof cronFields)[number]['name'];

type Props = {
  value?: string;
  onChange?: (val: string) => void;
  error?: string;
};

const CronInput = ({ value = '* * * * *', onChange, error }: Props) => {
  const [localCron, setLocalCron] = useState<Record<CronField, string>>({
    minutes: '',
    hours: '',
    dayOfMonth: '',
    month: '',
    dayOfWeek: '',
  });

  useEffect(() => {
    const parts = value.split(' ');
    if (parts.length === 5) {
      const [minutes, hours, dayOfMonth, month, dayOfWeek] = parts;
      const nextCron = {
        minutes: minutes === '*' ? '' : minutes,
        hours: hours === '*' ? '' : hours,
        dayOfMonth: dayOfMonth === '*' ? '' : dayOfMonth,
        month: month === '*' ? '' : month,
        dayOfWeek: dayOfWeek === '*' ? '' : dayOfWeek,
      };
      setLocalCron(nextCron);
    }
  }, [value]);

  const updateField =
    (field: CronField, maxLength: number, maxValue: number) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let input = e.target.value;

      if (input === '*') {
        input = '';
      } else if (!/^\d*$/.test(input)) {
        return;
      } else if (input.length > maxLength) {
        return;
      } else if (input.length === maxLength) {
        const numeric = parseInt(input, 10);
        if (numeric > maxValue) return;
      }

      const updated = { ...localCron, [field]: input };
      setLocalCron(updated);

      const cronString = [
        updated.minutes || '*',
        updated.hours || '*',
        updated.dayOfMonth || '*',
        updated.month || '*',
        updated.dayOfWeek || '*',
      ].join(' ');

      onChange?.(cronString);
    };

  /*
   * El scheduler evalua los cron en UTC, pero quien los carga piensa en la hora
   * de su operacion: escribir 9 en Horas dispara a las 9 UTC, que son las 4 de
   * la manana en Lima. Mientras haya una hora fija se muestra a que hora de
   * cada pais equivale, empezando por el pais de este panel.
   */
  const utcHour = /^\d{1,2}$/.test(localCron.hours)
    ? Number(localCron.hours)
    : null;
  const utcMinute = /^\d{1,2}$/.test(localCron.minutes)
    ? Number(localCron.minutes)
    : 0;

  const equivalences =
    utcHour === null || utcHour > 23
      ? null
      : [COUNTRY, ...COUNTRY_CODES.filter((code) => code !== COUNTRY)]
          .map(
            (code) =>
              `${utcTimeInCountry(utcHour, utcMinute, code)} en ${COUNTRY_LABEL[code]}`
          )
          .join(' · ');

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex gap-4'>
        {cronFields.map(({ name, label, maxLength, maxValue }) => (
          <div key={name} className='flex flex-col gap-2'>
            <TextField
              type='text'
              className='w-20'
              placeholder='*'
              value={localCron[name]}
              onChange={updateField(name, maxLength, maxValue)}
              error={!!error}
            />
            <p className='text-gray-500'>{label}</p>
          </div>
        ))}
      </div>
      <p className='text-sm text-gray-700 mt-1'>
        Resultado:{' '}
        <code className='font-mono'>
          {cronstrue.toString(
            [
              localCron.minutes || '*',
              localCron.hours || '*',
              localCron.dayOfMonth || '*',
              localCron.month || '*',
              localCron.dayOfWeek || '*',
            ].join(' '),
            { locale: 'es' }
          )}
        </code>
      </p>
      <p className='text-sm text-gray-700'>
        La hora se interpreta en <strong>UTC</strong>, no en la hora local.
        {equivalences ? (
          <>
            {' '}
            Las {String(utcHour).padStart(2, '0')}:
            {String(utcMinute).padStart(2, '0')} UTC son las {equivalences}.
          </>
        ) : (
          ' Dejá Horas vacío para que corra todas las horas.'
        )}
      </p>
      {error && <p className='text-red-500 text-sm'>{error}</p>}
    </div>
  );
};

export default CronInput;
